if (!customElements.get('lhc-testimonials-carousel')) {
  class LhcTestimonialsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lhc-track]');
      this.viewport = this.querySelector('[data-lhc-viewport]');
      this.prevButton = this.querySelector('[data-lhc-prev]');
      this.nextButton = this.querySelector('[data-lhc-next]');
      this.dotsWrapper = this.querySelector('[data-lhc-dots]');
      this.dots = this.dotsWrapper ? Array.from(this.dotsWrapper.querySelectorAll('[data-lhc-dot]')) : [];

      if (!this.track) return;

      this.loop = this.dataset.loop === 'true';
      this.swipeEnabled = this.dataset.swipe !== 'false';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.swipeEnabled) {
        this.track.style.overflowX = 'hidden';
      }

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.updateBoundaryState = this.updateBoundaryState.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);

      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index));
      });

      this.addEventListener('shopify:block:select', this.onBlockSelect);

      window.addEventListener('resize', this.updateBoundaryState);

      this.updateBoundaryState();
      this.startAutoplay();

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.updateBoundaryState);
    }

    getCards() {
      return Array.from(this.track.children);
    }

    getStep() {
      const cards = this.getCards();
      if (!cards.length) return 0;
      const style = window.getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || '0');
      return cards[0].getBoundingClientRect().width + gap;
    }

    isOverflowing() {
      return this.track.scrollWidth - this.track.clientWidth > 4;
    }

    updateBoundaryState() {
      const overflowing = this.isOverflowing();

      this.classList.toggle('lhc-testimonials__carousel--centered', !overflowing);

      if (!overflowing) {
        if (this.prevButton) this.prevButton.hidden = true;
        if (this.nextButton) this.nextButton.hidden = true;
        if (this.dotsWrapper) this.dotsWrapper.hidden = true;
        return;
      }

      if (this.dotsWrapper) this.dotsWrapper.hidden = this.dots.length < 2;

      if (this.loop) {
        if (this.prevButton) this.prevButton.disabled = false;
        if (this.nextButton) this.nextButton.disabled = false;
        return;
      }

      const atStart = this.track.scrollLeft <= 4;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;

      if (this.prevButton) this.prevButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;

      this.updateActiveDot();
    }

    updateActiveDot() {
      if (!this.dots.length) return;
      const step = this.getStep();
      if (!step) return;
      const index = Math.round(this.track.scrollLeft / step);
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    onTrackScroll() {
      window.clearTimeout(this._scrollTimeout);
      this._scrollTimeout = window.setTimeout(this.updateBoundaryState, 100);
    }

    scrollByStep(direction) {
      const step = this.getStep();
      if (!step) return;

      const atStart = this.track.scrollLeft <= 4;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;

      if (this.loop && direction > 0 && atEnd) {
        this.track.scrollTo({ left: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        return;
      }

      if (this.loop && direction < 0 && atStart) {
        this.track.scrollTo({ left: this.track.scrollWidth, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        return;
      }

      this.track.scrollBy({ left: step * direction, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    scrollToIndex(index) {
      const step = this.getStep();
      this.track.scrollTo({ left: step * index, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    onPrevClick() {
      this.scrollByStep(-1);
    }

    onNextClick() {
      this.scrollByStep(1);
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollByStep(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollByStep(-1);
      }
    }

    onBlockSelect(event) {
      const card = event.target.closest('[data-lhc-card]');
      if (!card || !this.track.contains(card)) return;
      card.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion || this._autoplayTimer) return;
      if (!this.isOverflowing()) return;
      this._autoplayTimer = window.setInterval(() => this.scrollByStep(1), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this._autoplayTimer) {
        window.clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }
  }

  customElements.define('lhc-testimonials-carousel', LhcTestimonialsCarousel);
}
